export interface ApiError {
  error: string;
  errorCode?: string;
}

let globalCreditLimitHandler: (() => void) | null = null;
type UnauthorizedHandler = (redirectPath?: string, options?: { accountDisabled?: boolean }) => void;

let globalUnauthorizedHandler: UnauthorizedHandler | null = null;
let originalFetch: typeof fetch | null = null;

export const setGlobalCreditLimitHandler = (handler: (() => void) | null): void => {
  globalCreditLimitHandler = handler;
};

export const setGlobalUnauthorizedHandler = (handler: UnauthorizedHandler | null): void => {
  globalUnauthorizedHandler = handler;
};

export const installGlobalApiInterceptors = (): void => {
  if (typeof window === 'undefined') {
    return;
  }

  if (originalFetch) {
    return;
  }

  originalFetch = window.fetch.bind(window);

  window.fetch = async (input: RequestInfo | URL, init?: RequestInit): Promise<Response> => {
    const response = await originalFetch!(input, init);

    if (!response.ok && response.status === 403 && globalCreditLimitHandler) {
      const clonedResponse = response.clone();
      try {
        const errorData: ApiError = await clonedResponse.json();
        if (errorData.errorCode === 'CREDIT_LIMIT_REACHED') {
          globalCreditLimitHandler();
        }
      } catch {
      }
    }

    if (!response.ok && response.status === 403 && globalUnauthorizedHandler) {
      const clonedResponse = response.clone();
      try {
        const errorData: ApiError = await clonedResponse.json();
        if (errorData.errorCode === 'ACCOUNT_DISABLED') {
          const currentPath =
            typeof window !== 'undefined'
              ? `${window.location.pathname}${window.location.search}`
              : undefined;
          globalUnauthorizedHandler(currentPath, { accountDisabled: true });
        }
      } catch {
      }
    }

    if (!response.ok && response.status === 401 && globalUnauthorizedHandler) {
      try {
        const currentPath = typeof window !== 'undefined'
          ? `${window.location.pathname}${window.location.search}`
          : undefined;
        globalUnauthorizedHandler(currentPath);
      } catch {
      }
    }

    return response;
  };
};

export const apiFetch = async (
  url: string,
  options?: RequestInit
): Promise<Response> => {
  return fetch(url, options);
};

export const apiFetchJson = async <T = any>(
  url: string,
  options?: RequestInit
): Promise<T> => {
  const response = await apiFetch(url, options);

  if (!response.ok) {
    const errorData: ApiError = await response.json().catch(() => ({
      error: `Request failed with status ${response.status}`,
    }));
    throw new Error(errorData.error || `Request failed with status ${response.status}`);
  }

  return response.json();
};

